import type { Dayjs } from "dayjs";

import {
  getPresetDateRange,
  PRESETS,
  type DateRange,
  type DateRangePickerValue,
  type PresetName,
} from "../date-range-picker";

export interface DateRangePickerBounds {
  minDate?: Dayjs;
  maxDate?: Dayjs;
  allowedPresetNames?: readonly PresetName[];
  resolvePresetDateRange?: (presetName: PresetName) => DateRange;
}

function isSameDateRangePickerValue(
  left: DateRangePickerValue,
  right: DateRangePickerValue,
) {
  if (left.type !== right.type) {
    return false;
  }

  if (left.type === "preset" && right.type === "preset") {
    return left.presetName === right.presetName;
  }

  if (left.type !== "dateRange" || right.type !== "dateRange") {
    return false;
  }

  return (
    left.dateRange.from.toJSON() === right.dateRange.from.toJSON() &&
    left.dateRange.to.toJSON() === right.dateRange.to.toJSON()
  );
}

function clampDayjsToDateRangeBounds(
  value: Dayjs,
  { minDate, maxDate }: DateRangePickerBounds,
) {
  if (minDate && value.isBefore(minDate)) {
    return minDate;
  }

  if (maxDate && value.isAfter(maxDate)) {
    return maxDate;
  }

  return value;
}

function clampDateRangeToBounds(
  value: DateRange,
  bounds: DateRangePickerBounds,
): DateRange {
  const from = clampDayjsToDateRangeBounds(value.from, bounds);
  const to = clampDayjsToDateRangeBounds(value.to, bounds);

  if (from.isAfter(to)) {
    return { from, to: from };
  }

  return { from, to };
}

function getAllowedDateRangePickerPresetNames({
  allowedPresetNames,
  minDate,
  maxDate,
  resolvePresetDateRange = getPresetDateRange,
}: DateRangePickerBounds): PresetName[] {
  const candidatePresetNames =
    allowedPresetNames ?? PRESETS.map(({ name }) => name);

  return candidatePresetNames.filter((name) => {
    const range = resolvePresetDateRange(name);

    return (
      (!minDate || !range.from.isBefore(minDate)) &&
      (!maxDate || !range.to.isAfter(maxDate))
    );
  });
}

function clampDateRangePickerValueToBounds(
  value: DateRangePickerValue,
  bounds: DateRangePickerBounds,
): DateRangePickerValue {
  const sourceDateRange =
    value.type === "preset"
      ? (bounds.resolvePresetDateRange ?? getPresetDateRange)(value.presetName)
      : value.dateRange;
  const clampedDateRange = clampDateRangeToBounds(sourceDateRange, bounds);

  if (value.type === "preset") {
    const allowedPresetNames =
      bounds.allowedPresetNames ?? getAllowedDateRangePickerPresetNames(bounds);

    if (allowedPresetNames.includes(value.presetName)) {
      return value;
    }
  }

  const clampedValue: DateRangePickerValue = {
    type: "dateRange",
    dateRange: clampedDateRange,
  };

  return isSameDateRangePickerValue(value, clampedValue) ? value : clampedValue;
}

export {
  clampDateRangePickerValueToBounds,
  clampDayjsToDateRangeBounds,
  getAllowedDateRangePickerPresetNames,
  isSameDateRangePickerValue,
};
